import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse, UserProfile, ChangePasswordDto } from '../models';

export interface JwtPayload extends UserProfile {
  exp?: number;
  iat?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private apiUrl = environment.apiUrl;

  // Signal for the current logged-in user profile
  readonly currentUser = signal<UserProfile | null>(null);

  // Computed states
  readonly isLoggedIn = computed(() => this.currentUser() !== null);
  readonly isSuperAdmin = computed(() => this.currentUser()?.role === 'SUPER_ADMIN');
  readonly isManager = computed(() => this.currentUser()?.role === 'MANAGER');
  readonly isEmployee = computed(() => this.currentUser()?.role === 'EMPLOYEE');
  readonly isAdminOrManager = computed(() => {
    const role = this.currentUser()?.role;
    return role === 'SUPER_ADMIN' || role === 'MANAGER';
  });

  constructor() {
    this.loadSession();
  }

  login(username: string, password: string): Observable<ApiResponse<{ token: string; user: UserProfile }>> {
    return this.http.post<ApiResponse<{ token: string; user: UserProfile }>>(
      `${this.apiUrl}/auth/login`,
      { username, password }
    ).pipe(
      tap(res => {
        if (res.success && res.data.token) {
          localStorage.setItem('token', res.data.token);
          this.currentUser.set(res.data.user);
        }
      })
    );
  }

  /**
   * 개발 환경 전용 기사(EMPLOYEE) 자동 로그인.
   * 운영 환경(production: true)에서는 서버 및 클라이언트에서 차단됩니다.
   */
  devAutoLogin(username?: string): Observable<ApiResponse<{ token: string; user: UserProfile }>> {
    return this.http.post<ApiResponse<{ token: string; user: UserProfile }>>(
      `${this.apiUrl}/auth/dev-auto-login`,
      username ? { username } : {}
    ).pipe(
      tap(res => {
        if (res.success && res.data.token) {
          localStorage.setItem('token', res.data.token);
          this.currentUser.set(res.data.user);
        }
      })
    );
  }

  changePassword(dto: ChangePasswordDto): Observable<ApiResponse<{ message: string }>> {
    return this.http.post<ApiResponse<{ message: string }>>(
      `${this.apiUrl}/auth/change-password`,
      dto
    );
  }

  logout(): void {
    localStorage.removeItem('token');
    this.currentUser.set(null);
    // Defer navigation to avoid conflict during active routing/bootstrap phase
    setTimeout(() => {
      this.router.navigate(['/login']);
    });
  }

  private loadSession(): void {
    if (typeof window === 'undefined') return;
    const token = localStorage.getItem('token');
    if (!token) return;

    // Decode token locally to set initial user state
    const decoded = this.decodeToken(token);
    if (decoded) {
      // Check if token is expired
      const isExpired = decoded.exp ? (decoded.exp * 1000 < Date.now()) : false;
      if (isExpired) {
        this.logout();
        return;
      }

      this.currentUser.set(decoded);
      // Fetch fresh profile from API to verify/sync
      this.http.get<ApiResponse<UserProfile>>(`${this.apiUrl}/auth/me`).subscribe({
        next: (res) => {
          if (res.success) {
            this.currentUser.set(res.data);
          } else {
            this.logout();
          }
        },
        error: () => {
          this.logout();
        }
      });
    } else {
      this.logout();
    }
  }

  private decodeToken(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Decode base64url to string
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload) as JwtPayload;
    } catch {
      return null;
    }
  }
}
